import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaRocket, FaMicrochip, FaUsers, FaHandshake } from 'react-icons/fa';

import Header from '../page/Header';
import Footer from '../page/Footer';
import JoinButton from '../buttons/JoinButton';

// Section data with a simple structure
const sections = [
  {
    title: "Innovating Tomorrow, Today.",
    icon: <FaRocket />,
    content: "At Girraj Technology, we don't just build software; we engineer the future. Our vision is to empower businesses with transformative digital solutions, leveraging bleeding-edge AI, quantum-inspired algorithms, and immersive XR experiences to redefine possibilities. We're the architects of your next-gen success."
  },
  {
    title: "Our Digital Alchemy.",
    icon: <FaMicrochip />,
    list: [
      "AI-Driven Hyperautomation & Predictive Analytics",
      "Decentralized Web3 & Blockchain Ecosystems",
      "Immersive AR/VR/Metaverse Solutions",
      "Cloud-Native & Serverless Architecture",
      "Quantum Computing Readiness & Optimization",
      "Cyber-Physical Security & Data Sovereignty"
    ]
  },
  {
    title: "The Minds Behind the Magic.",
    icon: <FaUsers />,
    content: "Our collective is a synergistic blend of visionary engineers, data scientists, UX futurists, and ethical AI specialists. We thrive on audacious challenges, collaborative ideation, and a relentless pursuit of digital perfection. Each team member is a pioneer, pushing boundaries and shaping the technological landscape."
  },
  {
    title: "Beyond Services: A Partnership for Progress.",
    icon: <FaHandshake />,
    list: [
      "Strategic Digital Transformation Roadmapping",
      "Bespoke AI & Machine Learning Model Development",
      "Secure Enterprise Blockchain Solutions",
      "Scalable Cloud & Edge Computing Deployments",
      "Interactive Digital Twin & Simulation Platforms",
      "Continuous Innovation & Tech Advisory"
    ]
  }
];

const AboutUsContainer = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Poppins:wght@400;600&display=swap');
  background-color: #0d0c1d; // Deep, calm blue
  color: #f0f0f5;
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const MainContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  padding: 4rem 1.5rem;
  margin: 0 auto;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 4rem 0;
  h1 {
    font-size: clamp(2.5rem, 6vw, 4.5rem);
    font-family: 'Playfair Display', serif;
    color: #e6b325;
    margin-bottom: 1rem;
    font-weight: 700;
  }
  p {
    font-size: clamp(1rem, 2vw, 1.3rem);
    max-width: 700px;
    margin: 0 auto 2rem;
    line-height: 1.6;
    color: #c0c0d0;
  }
`;

const SectionCard = styled(motion.section)`
  display: flex;
  flex-direction: ${({ reverse }) => (reverse ? 'row-reverse' : 'row')};
  align-items: center;
  gap: 3rem;
  padding: 3rem;
  margin: 3rem 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
    gap: 1.5rem;
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  font-size: 5rem;
  color: #4da6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(77, 166, 255, 0.1);
  border-radius: 50%;
  width: 100px;
  height: 100px;
  padding: 1rem;
`;

const TextContent = styled.div`
  flex: 1;
`;

const SectionHeading = styled.h2`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  font-family: 'Playfair Display', serif;
  color: #e6b325;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const SectionParagraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #d0d0e0;
`;

const SectionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0 0;
`;

const ListItem = styled.li`
  position: relative;
  font-size: 1.1rem;
  line-height: 2;
  color: #d0d0e0;
  padding-left: 2rem;
  
  &::before {
    content: '•';
    color: #4da6ff;
    font-size: 1.5rem;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  @media (max-width: 768px) {
    padding-left: 0;
    &::before {
      display: none;
    }
  }
`;

const AboutUs = () => {
  return (
    <>
      <Header />
      <AboutUsContainer>
        <MainContent>
          <HeroSection>
            <h1>About Us</h1>
            <p>
              Pioneering the future of digital innovation with cutting-edge
              AI, blockchain, and immersive tech solutions.
            </p>
            <JoinButton />
          </HeroSection>

          {sections.map((section, index) => (
            <SectionCard
              key={index}
              reverse={index % 2 !== 0}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <IconWrapper>{section.icon}</IconWrapper>
              <TextContent>
                <SectionHeading>{section.title}</SectionHeading>
                {section.content ? (
                  <SectionParagraph>{section.content}</SectionParagraph>
                ) : (
                  <SectionList>
                    {section.list.map((item, i) => (
                      <ListItem key={i}>{item}</ListItem>
                    ))}
                  </SectionList>
                )}
              </TextContent>
            </SectionCard>
          ))}
        </MainContent>
      </AboutUsContainer>
      <Footer />
    </>
  );
};

export default AboutUs;